import { computed, ref, watch } from "vue";
import { getDatabase, ref as dbRef, set } from "firebase/database";
import { authStore } from "@/store/auth";
import { useHaptics } from "@/composables/useHaptics";

const THEME_CACHE_KEY = "al-theme";
const MODES = ["light", "dark", "system"];
// Literal mirrors of --al-surface for the native status bar, which cannot
// resolve CSS vars. Keep in lockstep with utilities.css.
const SURFACE_HEX = { light: "#ffffff", dark: "#081a31" };

const mode = ref("system");
const systemPrefersDark = ref(false);
// Route gate: dark only renders on in-scope (authenticated) surfaces.
// Marketing/public/legal pages stay light regardless of the preference.
const themedSurface = ref(isLikelyInScope());

let initialized = false;

const resolved = computed(() =>
  mode.value === "system" ? (systemPrefersDark.value ? "dark" : "light") : mode.value
);

// Pre-router heuristic mirroring the boot snippet in public/index.html;
// the router's afterEach takes ownership on first navigation.
function isLikelyInScope() {
  if (typeof window === "undefined") return false;
  if (window.Capacitor?.isNativePlatform?.() === true) return true;
  const loc = `${location.pathname}${location.hash}`;
  return /dashboard/.test(loc);
}

function readCache() {
  try {
    const value = localStorage.getItem(THEME_CACHE_KEY);
    return MODES.includes(value) ? value : "system";
  } catch {
    return "system";
  }
}

function writeCache(value) {
  try {
    localStorage.setItem(THEME_CACHE_KEY, value);
  } catch {
    // Storage unavailable (private mode) — System still works per-session.
  }
}

function applyDom() {
  const dark = resolved.value === "dark" && themedSurface.value;
  if (dark) {
    document.documentElement.setAttribute("data-theme", "dark");
  } else {
    // Light is the absence of the attribute, matching the boot snippet.
    document.documentElement.removeAttribute("data-theme");
  }
  syncNativeChrome();
}

async function syncNativeChrome() {
  if (window.Capacitor?.isNativePlatform?.() !== true) return;
  const effective = themedSurface.value ? resolved.value : "light";
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    // Capacitor semantics: Style.Dark = light text (for dark backgrounds),
    // Style.Light = dark text (for light backgrounds).
    await StatusBar.setStyle({
      style: effective === "dark" ? Style.Dark : Style.Light,
    }).catch(() => {});
    if (window.Capacitor.getPlatform?.() === "android") {
      // Android-only; best-effort on pre-15 (Android 15/16 edge-to-edge
      // ignores it and shows whatever html/body paints behind the inset).
      await StatusBar.setBackgroundColor({ color: SURFACE_HEX[effective] }).catch(() => {});
    }
  } catch {
    // Plugin unavailable — nothing to sync.
  }
}

function persistRemote(value) {
  const userId = authStore.getUserId();
  if (!userId) return;
  set(dbRef(getDatabase(), `users/${userId}/theme`), value).catch((error) => {
    // Best-effort: the localStorage cache is the immediate source of truth.
    console.error("Failed to sync theme preference:", error);
  });
}

function setMode(next) {
  if (!MODES.includes(next) || next === mode.value) return;
  mode.value = next;
  writeCache(next);
  persistRemote(next);
  useHaptics().impactLight();
}

/** Adopt the RTDB value on login without re-writing RTDB or firing haptics. */
function adoptStoredMode(next) {
  if (!MODES.includes(next) || next === mode.value) return;
  mode.value = next;
  writeCache(next);
}

function setThemedSurface(value) {
  themedSurface.value = !!value;
}

export function initTheme() {
  if (typeof window === "undefined") return;
  if (initialized) return;
  initialized = true;
  mode.value = readCache();
  if (window.matchMedia) {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    systemPrefersDark.value = media.matches;
    const onChange = (event) => {
      systemPrefersDark.value = event.matches;
    };
    if (media.addEventListener) media.addEventListener("change", onChange);
    else media.addListener(onChange); // older iOS WebViews
  }
  watch([resolved, themedSurface], applyDom, { immediate: true });
}

export function useTheme() {
  return { mode, resolved, setMode, adoptStoredMode, setThemedSurface };
}
